/**
 * **Total · Per month** — `docs/proposal.md` §6.5, board 13 cells 1–2.
 *
 * Some customers hold the whole price in their head and some hold the monthly
 * payment, and a document that states only one of them is asking half its
 * readers to do arithmetic beside a signature line. So the total card carries
 * a two-segment control, and the summary rail, the bottom bar and the
 * Included heading follow it: on **Per month** they lead with "$950 / month"
 * and say the total second, on **Total** the other way round. **The total
 * itself never changes**: it is one price said two ways, not two prices, and
 * whichever leads, the other is beside it.
 *
 * The control is only drawn where `view.month` is non-null, which is the
 * server's answer to "can this quote honestly be read by the period at all"
 * (§4.4). A quote mixing a monthly plan and a weekly one has no single "per
 * month" and gets no toggle.
 *
 * The choice is **per browser, not per quote**: `sessionStorage`, so it
 * survives a scroll and a reload of the same tab and reaches nothing else. It
 * is a way of reading, not an answer the office needs.
 *
 * **Where nobody has chosen, the quote's own price view decides** (Aaron's
 * review, item 2.2). A quote whose estimator set "Price shown as" to the
 * billing plan is one the office means to be read by the month — every visit
 * card already says "$950 / month" — so the total card opens on **Per month**
 * and the rail and the bar lead with the monthly figure. "Per visit" and
 * "Both" open on **Total**, as every quote did before.
 */
import { useCallback, useState } from 'react'
import type { MonthSplit, MonthView, ProposalView } from '@rowted/shared'
import { cn } from '../../lib/cn.js'

export type MonthMode = 'total' | 'period'

const KEY = 'rowted.proposal.months'

/**
 * Which way this reader is looking at it, remembered for the tab.
 *
 * **Only a choice is written down, never the default.** It used to be stored
 * from an effect on every render, which made the opening value look like
 * something the reader had picked: one plan-view quote opened on Per month
 * would have written `period`, and so would one opened on Total — and the
 * next quote in the tab would then read that instead of its own default.
 * So storage is touched in the setter, which only a tap calls.
 *
 * Every read and write is in a `try` — a private window, blocked site data
 * or a thumbnail capture can each make `sessionStorage` throw rather than
 * return nothing, and a total card that fails to render is a worse outcome
 * than one that opens on its default.
 */
export function useMonthMode(preferPeriod: boolean): [MonthMode, (mode: MonthMode) => void] {
  const [chosen, setChosen] = useState<MonthMode | null>(() => {
    try {
      const stored = window.sessionStorage.getItem(KEY)
      return stored === 'period' || stored === 'total' ? stored : null
    } catch { return null }
  })

  const choose = useCallback((next: MonthMode) => {
    setChosen(next)
    try { window.sessionStorage.setItem(KEY, next) } catch { /* not worth a word */ }
  }, [])

  return [chosen ?? (preferPeriod ? 'period' : 'total'), choose]
}

/**
 * Whether **Total · Per month** is on offer at all, for what is ticked now.
 *
 * The server's `view.month` says the quote has one honest cadence (§4.4);
 * this adds that every question has an answer and that the selection on
 * screen actually puts something on such a plan. A customer who picks the
 * one-off option out of a group has nothing monthly left, and a control that
 * then switched to an empty "Every month" section would be the page offering
 * a reading of the quote that does not apply to the quote they are reading.
 *
 * One function, because the toggle, the rail, the bar and the Included
 * heading all lead with the month exactly when it is offered **and** chosen,
 * and four copies of the condition are four places for one of them to lead
 * with a figure the toggle does not exist to explain.
 */
export function monthOffered(
  view: ProposalView, split: MonthSplit | null, ready: boolean,
): boolean {
  return ready && view.month !== null && split !== null && split.rows.length > 0
}

/** "Per month", "Per week", "Per 2 weeks" — the label follows the cadence. */
export const perLabel = (month: MonthView): string => `Per ${month.label}`

export function MonthToggle({ month, mode, onMode }: {
  month: MonthView
  mode: MonthMode
  onMode: (mode: MonthMode) => void
}) {
  return (
    <div
      role="radiogroup"
      aria-label="How to read the total"
      /*
       * The track is the ink — 34px of pill — and the **buttons** are 44px,
       * which is why the padding is on them and not here. A 34px-tall button
       * with padding inside a fixed height is still a 34px target: §13 asks
       * for the box, and a segmented control small enough to miss is one
       * people give up on rather than report.
       */
      className="inline-flex gap-[3px] rounded-[12px] bg-line-soft p-[3px]"
    >
      {([['total', 'Total'], ['period', perLabel(month)]] as const).map(([value, label]) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={mode === value}
          onClick={() => onMode(value)}
          /*
           * 34px drawn, 44px hit (§14 item 8): the target reaches 5px past the
           * track above and below, so the control is board 2's size and still
           * a thumb's.
           */
          className="relative flex items-center before:absolute before:inset-x-0 before:-inset-y-[8px]
                     before:content-['']"
        >
          {/* `min-w-[96px]` is the mockup's, so "Per 2 weeks" does not make
            * one segment twice the width of the other. */}
          <span className={cn(`flex h-[34px] min-w-[96px] items-center justify-center
                               rounded-[9px] px-3 text-[13.5px] font-[650]`,
            mode === value
              ? 'bg-surface text-text shadow-[0_1px_3px_rgba(0,0,0,0.14)]'
              : 'text-text-muted')}>
            {label}
          </span>
        </button>
      ))}
    </div>
  )
}
